import React, { useState, useEffect, useCallback } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Label } from '../components/ui/label';
import { 
  Tags, CheckCircle, XCircle, AlertTriangle, Copy, RefreshCw,
  FileText, ArrowRight, Lightbulb, Shield, BarChart3, Search,
  ChevronDown, Info, Zap, Target, Users, Globe2, Tv, Box
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function TaxonomyBuilder() {
  const [activeTab, setActiveTab] = useState('builder');
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(false);
  
  // Reference data
  const [referenceData, setReferenceData] = useState(null);
  
  // Builder state
  const [formData, setFormData] = useState({
    market: '',
    brand: 'mediakshetra',
    funnel: '',
    objective: '',
    audience: '',
    initiative: '',
    channel: '',
    format: '',
    version: 1
  });
  
  // Validation state
  const [validationMode, setValidationMode] = useState('strict');
  const [validationResult, setValidationResult] = useState(null);
  const [generatedName, setGeneratedName] = useState('');
  
  // Entities state
  const [entities, setEntities] = useState([]);
  const [entitiesLoading, setEntitiesLoading] = useState(false);
  
  // Parse state
  const [parseInput, setParseInput] = useState('');
  const [parseResult, setParseResult] = useState(null);
  
  // Demo mode state
  const [demoEntities, setDemoEntities] = useState([]);

  useEffect(() => {
    fetchReferenceData();
    fetchEntities();
    fetchDemoEntities();
  }, []);

  const fetchReferenceData = async () => {
    try {
      const response = await fetch(`${API_URL}/api/gios/taxonomy/reference`);
      if (response.ok) {
        const data = await response.json();
        setReferenceData(data);
      }
    } catch (error) {
      console.error('Failed to fetch reference data:', error);
      toast.error('Failed to load reference data');
    }
  };

  const fetchEntities = async () => {
    setEntitiesLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/gios/taxonomy/entities`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setEntities(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      console.error('Failed to fetch entities:', error);
    } finally {
      setEntitiesLoading(false);
    }
  };

  const fetchDemoEntities = async () => {
    try {
      const response = await fetch(`${API_URL}/api/gios/demo/taxonomy-entities`);
      if (response.ok) {
        const data = await response.json();
        setDemoEntities(data);
      }
    } catch (error) {
      console.error('Failed to fetch demo entities:', error);
    }
  };

  // Generate name preview in real-time
  const generateNamePreview = useCallback(() => {
    const parts = [
      formData.market || '___',
      formData.brand || '___',
      formData.funnel || '___',
      formData.objective || '___',
      formData.audience || '___',
      formData.initiative?.toLowerCase().replace(/\s+/g, '_') || '___',
      formData.channel || '___',
      formData.format || '___',
      `v${formData.version}`
    ];
    return parts.join('-');
  }, [formData]);

  useEffect(() => {
    setGeneratedName(generateNamePreview());
  }, [generateNamePreview]);

  const handleValidate = async () => {
    setValidating(true);
    setValidationResult(null);
    
    try {
      const response = await fetch(`${API_URL}/api/gios/taxonomy/validate`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          validation_mode: validationMode
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        setValidationResult(data);
        setGeneratedName(data.generated_name);
        
        if (data.valid) {
          toast.success(`Validation passed! Integrity Score: ${data.integrity_score}`);
        } else {
          toast.error(`Validation failed: ${data.blocking_issues} blocking issues`);
        }
      } else {
        toast.error('Validation failed');
      }
    } catch (error) {
      toast.error('Failed to validate');
    } finally {
      setValidating(false);
    }
  };

  const handleCreateEntity = async () => {
    if (!validationResult?.valid) {
      toast.error('Please validate the name first');
      return;
    }
    
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/gios/taxonomy/entities`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          client_id: 'default',
          entity_type: 'campaign',
          validation_mode: validationMode
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success('Campaign entity created successfully!');
        fetchEntities();
        
        // Reset form
        setFormData({
          market: '',
          brand: 'mediakshetra',
          funnel: '',
          objective: '',
          audience: '',
          initiative: '',
          channel: '',
          format: '',
          version: 1
        });
        setValidationResult(null);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create entity');
      }
    } catch (error) {
      toast.error('Failed to create entity');
    } finally {
      setLoading(false);
    }
  };

  const handleParse = async () => {
    if (!parseInput.trim()) {
      toast.error('Please enter a campaign name to parse');
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/gios/taxonomy/parse?entity_name=${encodeURIComponent(parseInput)}`, {
        method: 'POST',
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setParseResult(data);
        toast.success('Campaign name parsed successfully');
      } else {
        toast.error('Failed to parse name');
      }
    } catch (error) {
      toast.error('Failed to parse name');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const getIntegrityScoreColor = (score) => {
    if (score >= 80) return 'text-emerald-400';
    if (score >= 60) return 'text-amber-400';
    return 'text-red-400';
  };

  const getIntegrityScoreBg = (score) => {
    if (score >= 80) return 'bg-emerald-500/20';
    if (score >= 60) return 'bg-amber-500/20';
    return 'bg-red-500/20';
  };

  const getSeverityBadge = (severity) => {
    const colors = {
      low: 'bg-blue-500/20 text-blue-400',
      medium: 'bg-amber-500/20 text-amber-400',
      high: 'bg-red-500/20 text-red-400',
      critical: 'bg-red-600/30 text-red-300'
    };
    return colors[severity] || colors.low;
  };

  const renderDropdown = (fieldKey, label, options, icon) => {
    const Icon = icon;
    const currentValue = formData[fieldKey];
    
    return (
      <div className="space-y-2">
        <Label className="text-sm text-zinc-400 flex items-center gap-2">
          <Icon className="w-3.5 h-3.5" />
          {label}
        </Label>
        <select
          value={currentValue}
          onChange={(e) => setFormData({ ...formData, [fieldKey]: e.target.value })}
          className="w-full h-10 px-3 rounded-md bg-zinc-800/50 border border-zinc-700 text-sm text-zinc-100 focus:outline-none focus:ring-2 focus:ring-primary/50"
          data-testid={`taxonomy-${fieldKey}`}
        >
          <option value="">Select {label}</option>
          {options && Object.entries(options).map(([code, name]) => (
            <option key={code} value={code}>
              {code.toUpperCase()} - {typeof name === 'object' ? name.name : name}
            </option>
          ))}
        </select>
      </div>
    );
  };

  return (
    <DashboardLayout>
      <div className="space-y-6 p-6" data-testid="taxonomy-builder-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/10">
                <Tags className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h1 className="text-2xl font-semibold tracking-tight">Taxonomy Builder</h1>
                <p className="text-sm text-muted-foreground">
                  Master Campaign Naming Framework
                </p>
              </div>
            </div>
          </div>
          <Badge variant="outline" className="text-xs px-3 py-1 bg-emerald-500/10 text-emerald-400 border-emerald-500/20">
            GIOS v2.0
          </Badge>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-zinc-800/50 border border-zinc-700">
            <TabsTrigger value="builder" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
              <Zap className="w-4 h-4 mr-2" />
              Builder
            </TabsTrigger>
            <TabsTrigger value="entities" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
              <FileText className="w-4 h-4 mr-2" />
              Entities
            </TabsTrigger>
            <TabsTrigger value="parse" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
              <Search className="w-4 h-4 mr-2" />
              Parse
            </TabsTrigger>
            <TabsTrigger value="demo" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
              <Lightbulb className="w-4 h-4 mr-2" />
              Demo
            </TabsTrigger>
          </TabsList>

          {/* Builder Tab */}
          <TabsContent value="builder" className="space-y-6 mt-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Form Section */}
              <div className="lg:col-span-2 space-y-6">
                <Card className="bg-zinc-900/50 border-zinc-800">
                  <CardHeader className="pb-4">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Target className="w-5 h-5 text-primary" />
                      Campaign Components
                    </CardTitle>
                    <CardDescription>
                      Build structured campaign names using the MediaKshetra taxonomy
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {/* Row 1: Market, Brand */}
                    <div className="grid grid-cols-2 gap-4">
                      {renderDropdown('market', 'Market', referenceData?.markets, Globe2)}
                      <div className="space-y-2">
                        <Label className="text-sm text-zinc-400 flex items-center gap-2">
                          <Box className="w-3.5 h-3.5" />
                          Brand
                        </Label>
                        <Input
                          value={formData.brand}
                          onChange={(e) => setFormData({ ...formData, brand: e.target.value.toLowerCase().replace(/\s+/g, '_') })}
                          placeholder="brand_name"
                          className="bg-zinc-800/50 border-zinc-700"
                          data-testid="taxonomy-brand"
                        />
                      </div>
                    </div>

                    {/* Row 2: Funnel, Objective */}
                    <div className="grid grid-cols-2 gap-4">
                      {renderDropdown('funnel', 'Funnel Stage', referenceData?.funnels, BarChart3)}
                      {renderDropdown('objective', 'Objective', referenceData?.objectives, Target)}
                    </div>

                    {/* Row 3: Audience, Initiative */}
                    <div className="grid grid-cols-2 gap-4">
                      {renderDropdown('audience', 'Audience', referenceData?.audiences, Users)}
                      <div className="space-y-2">
                        <Label className="text-sm text-zinc-400 flex items-center gap-2">
                          <Lightbulb className="w-3.5 h-3.5" />
                          Initiative
                        </Label>
                        <Input
                          value={formData.initiative}
                          onChange={(e) => setFormData({ ...formData, initiative: e.target.value })}
                          placeholder="q1_launch"
                          className="bg-zinc-800/50 border-zinc-700"
                          data-testid="taxonomy-initiative"
                        />
                      </div>
                    </div>

                    {/* Row 4: Channel, Format */}
                    <div className="grid grid-cols-2 gap-4">
                      {renderDropdown('channel', 'Channel', referenceData?.platforms, Tv)}
                      {renderDropdown('format', 'Format', referenceData?.formats, Box)}
                    </div>

                    {/* Row 5: Version */}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-sm text-zinc-400">Version</Label>
                        <Input
                          type="number"
                          min={1}
                          value={formData.version}
                          onChange={(e) => setFormData({ ...formData, version: parseInt(e.target.value) || 1 })}
                          className="bg-zinc-800/50 border-zinc-700 w-32"
                          data-testid="taxonomy-version"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-sm text-zinc-400">Validation Mode</Label>
                        <select
                          value={validationMode}
                          onChange={(e) => setValidationMode(e.target.value)}
                          className="h-10 px-3 rounded-md bg-zinc-800/50 border border-zinc-700 text-sm text-zinc-100 focus:outline-none focus:ring-2 focus:ring-primary/50"
                          data-testid="validation-mode"
                        >
                          <option value="strict">STRICT - Reject invalid</option>
                          <option value="assisted">ASSISTED - Warn only</option>
                          <option value="log_only">LOG_ONLY - Log issues</option>
                        </select>
                      </div>
                    </div>

                    {/* Generated Name Preview */}
                    <div className="p-4 rounded-lg bg-zinc-800/30 border border-zinc-700">
                      <Label className="text-xs text-zinc-500 uppercase tracking-wider">Generated Name</Label>
                      <div className="flex items-center justify-between mt-2">
                        <code className="text-sm font-mono text-primary break-all">
                          {generatedName}
                        </code>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => copyToClipboard(generatedName)}
                          className="ml-2"
                        >
                          <Copy className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex gap-3">
                      <Button
                        onClick={handleValidate}
                        disabled={validating || !formData.market || !formData.funnel || !formData.objective}
                        className="flex-1"
                        data-testid="validate-btn"
                      >
                        {validating ? (
                          <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Shield className="w-4 h-4 mr-2" />
                        )}
                        Validate
                      </Button>
                      <Button
                        onClick={handleCreateEntity}
                        disabled={loading || !validationResult?.valid}
                        variant="outline"
                        className="flex-1"
                        data-testid="create-entity-btn"
                      >
                        {loading ? (
                          <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <ArrowRight className="w-4 h-4 mr-2" />
                        )}
                        Create Entity
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Validation Result */}
              <div className="space-y-6">
                <Card className="bg-zinc-900/50 border-zinc-800">
                  <CardHeader className="pb-4">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Shield className="w-5 h-5 text-primary" />
                      Validation Result
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {validationResult ? (
                      <div className="space-y-4">
                        {/* Status */}
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-zinc-400">Status</span>
                          {validationResult.valid ? (
                            <Badge className="bg-emerald-500/20 text-emerald-400">
                              <CheckCircle className="w-3.5 h-3.5 mr-1" />
                              Valid
                            </Badge>
                          ) : (
                            <Badge className="bg-red-500/20 text-red-400">
                              <XCircle className="w-3.5 h-3.5 mr-1" />
                              Invalid
                            </Badge>
                          )}
                        </div>

                        {/* Integrity Score */}
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-zinc-400">Integrity Score</span>
                          <div className={`flex items-center gap-2 px-3 py-1 rounded-full ${getIntegrityScoreBg(validationResult.integrity_score)}`}>
                            <span className={`text-lg font-bold ${getIntegrityScoreColor(validationResult.integrity_score)}`}>
                              {validationResult.integrity_score}
                            </span>
                            <span className="text-xs text-zinc-500">/100</span>
                          </div>
                        </div>

                        {/* Validation Mode */}
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-zinc-400">Mode</span>
                          <Badge variant="outline" className="text-xs">
                            {validationResult.validation_mode?.toUpperCase()}
                          </Badge>
                        </div>

                        {/* Issue Count */}
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-zinc-400">Issues</span>
                          <div className="flex gap-2">
                            {validationResult.blocking_issues > 0 && (
                              <Badge className="bg-red-500/20 text-red-400">
                                {validationResult.blocking_issues} blocking
                              </Badge>
                            )}
                            {validationResult.warning_issues > 0 && (
                              <Badge className="bg-amber-500/20 text-amber-400">
                                {validationResult.warning_issues} warnings
                              </Badge>
                            )}
                            {validationResult.issue_count === 0 && (
                              <Badge className="bg-emerald-500/20 text-emerald-400">
                                No issues
                              </Badge>
                            )}
                          </div>
                        </div>

                        {/* Issues List */}
                        {validationResult.issues?.length > 0 && (
                          <div className="mt-4 space-y-2">
                            <Label className="text-xs text-zinc-500 uppercase tracking-wider">Issues</Label>
                            {validationResult.issues.map((issue, idx) => (
                              <div
                                key={idx}
                                className="p-3 rounded-md bg-zinc-800/30 border border-zinc-700"
                              >
                                <div className="flex items-start gap-2">
                                  {issue.action === 'block' ? (
                                    <XCircle className="w-4 h-4 text-red-400 mt-0.5" />
                                  ) : issue.action === 'warn' ? (
                                    <AlertTriangle className="w-4 h-4 text-amber-400 mt-0.5" />
                                  ) : (
                                    <Info className="w-4 h-4 text-blue-400 mt-0.5" />
                                  )}
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-1">
                                      <Badge className={`text-[10px] ${getSeverityBadge(issue.severity)}`}>
                                        {issue.severity}
                                      </Badge>
                                      <span className="text-[10px] text-zinc-500">{issue.layer}</span>
                                    </div>
                                    <p className="text-xs text-zinc-300">{issue.message}</p>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-zinc-500">
                        <Shield className="w-12 h-12 mx-auto mb-3 opacity-20" />
                        <p className="text-sm">Fill in the form and click Validate</p>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Quick Reference */}
                <Card className="bg-zinc-900/50 border-zinc-800">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <Info className="w-4 h-4 text-primary" />
                      Quick Reference
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-xs space-y-2">
                    <p className="text-zinc-400">
                      <strong className="text-zinc-300">Funnel-Objective Rules:</strong>
                    </p>
                    <ul className="space-y-1 text-zinc-500">
                      <li>• AW (Awareness): TR, RC, EN, VV, BR</li>
                      <li>• CO (Consideration): TR, LD, EN, AI, VV</li>
                      <li>• CV (Conversion): LD, CV, AI, SL</li>
                      <li>• RT (Retention): CV, SL</li>
                      <li>• LY (Loyalty): EN</li>
                    </ul>
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>

          {/* Entities Tab */}
          <TabsContent value="entities" className="mt-6">
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-lg">Campaign Entities</CardTitle>
                    <CardDescription>All validated campaign naming entities</CardDescription>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={fetchEntities}
                    disabled={entitiesLoading}
                  >
                    <RefreshCw className={`w-4 h-4 mr-2 ${entitiesLoading ? 'animate-spin' : ''}`} />
                    Refresh
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {entities.length > 0 ? (
                  <div className="space-y-3">
                    {entities.map((entity, idx) => (
                      <div
                        key={entity.entity_id || idx}
                        className="p-4 rounded-lg bg-zinc-800/30 border border-zinc-700 hover:border-zinc-600 transition-colors"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <code className="text-sm font-mono text-primary">
                              {entity.entity_name}
                            </code>
                            <div className="flex items-center gap-3 mt-2">
                              <Badge variant="outline" className="text-[10px]">
                                {entity.entity_type}
                              </Badge>
                              <span className="text-[10px] text-zinc-500">
                                Market: {entity.market_code?.toUpperCase()}
                              </span>
                              <span className="text-[10px] text-zinc-500">
                                Platform: {entity.platform_code?.toUpperCase()}
                              </span>
                              <span className="text-[10px] text-zinc-500">
                                Funnel: {entity.funnel_stage?.toUpperCase()}
                              </span>
                            </div>
                          </div>
                          <div className={`px-3 py-1 rounded-full ${getIntegrityScoreBg(entity.integrity_score)}`}>
                            <span className={`text-sm font-bold ${getIntegrityScoreColor(entity.integrity_score)}`}>
                              {entity.integrity_score}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 text-zinc-500">
                    <FileText className="w-12 h-12 mx-auto mb-3 opacity-20" />
                    <p>No entities created yet</p>
                    <p className="text-xs mt-1">Create your first entity using the Builder tab</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Parse Tab */}
          <TabsContent value="parse" className="mt-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card className="bg-zinc-900/50 border-zinc-800">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Search className="w-5 h-5 text-primary" />
                    Parse Campaign Name
                  </CardTitle>
                  <CardDescription>
                    Extract structured components from an existing campaign name
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label>Campaign Name</Label>
                    <Input
                      value={parseInput}
                      onChange={(e) => setParseInput(e.target.value)}
                      placeholder="sg-mediakshetra-aw-br-pros-launch-gg-vd-v1"
                      className="bg-zinc-800/50 border-zinc-700 font-mono"
                      data-testid="parse-input"
                    />
                  </div>
                  <Button
                    onClick={handleParse}
                    disabled={!parseInput.trim()}
                    className="w-full"
                    data-testid="parse-btn"
                  >
                    <Search className="w-4 h-4 mr-2" />
                    Parse Name
                  </Button>
                </CardContent>
              </Card>

              <Card className="bg-zinc-900/50 border-zinc-800">
                <CardHeader>
                  <CardTitle className="text-lg">Parse Result</CardTitle>
                </CardHeader>
                <CardContent>
                  {parseResult ? (
                    <div className="space-y-3">
                      {Object.entries(parseResult).map(([key, value]) => {
                        if (key === 'raw_name' || key === 'parsed_successfully') return null;
                        return (
                          <div key={key} className="flex items-center justify-between py-2 border-b border-zinc-800 last:border-0">
                            <span className="text-sm text-zinc-400 capitalize">{key.replace(/_/g, ' ')}</span>
                            <span className="text-sm font-medium">
                              {value?.toString() || '-'}
                            </span>
                          </div>
                        );
                      })}
                      <div className="pt-2">
                        <Badge className={parseResult.parsed_successfully ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}>
                          {parseResult.parsed_successfully ? 'Successfully Parsed' : 'Partial Parse'}
                        </Badge>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-8 text-zinc-500">
                      <Search className="w-12 h-12 mx-auto mb-3 opacity-20" />
                      <p className="text-sm">Enter a campaign name to parse</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Demo Tab */}
          <TabsContent value="demo" className="mt-6">
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Lightbulb className="w-5 h-5 text-amber-400" />
                      Demo Taxonomy Entities
                    </CardTitle>
                    <CardDescription>
                      Sample entities demonstrating validation states (3 valid, 2 invalid, 1 override)
                    </CardDescription>
                  </div>
                  <Badge className="bg-amber-500/20 text-amber-400">
                    Investor Demo
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {demoEntities.map((entity, idx) => (
                    <div
                      key={entity.entity_id || idx}
                      className={`p-4 rounded-lg border transition-colors ${
                        entity.status === 'valid' 
                          ? 'bg-emerald-500/5 border-emerald-500/20' 
                          : entity.status === 'invalid'
                          ? 'bg-red-500/5 border-red-500/20'
                          : 'bg-amber-500/5 border-amber-500/20'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            {entity.status === 'valid' && <CheckCircle className="w-4 h-4 text-emerald-400" />}
                            {entity.status === 'invalid' && <XCircle className="w-4 h-4 text-red-400" />}
                            {entity.status === 'override' && <AlertTriangle className="w-4 h-4 text-amber-400" />}
                            <Badge className={`text-[10px] ${
                              entity.status === 'valid' ? 'bg-emerald-500/20 text-emerald-400' :
                              entity.status === 'invalid' ? 'bg-red-500/20 text-red-400' :
                              'bg-amber-500/20 text-amber-400'
                            }`}>
                              {entity.status.toUpperCase()}
                            </Badge>
                          </div>
                          <code className={`text-sm font-mono ${
                            entity.status === 'valid' ? 'text-emerald-300' :
                            entity.status === 'invalid' ? 'text-red-300' :
                            'text-amber-300'
                          }`}>
                            {entity.entity_name}
                          </code>
                          {entity.validation_result?.issues?.length > 0 && (
                            <div className="mt-2 space-y-1">
                              {entity.validation_result.issues.map((issue, issueIdx) => (
                                <p key={issueIdx} className="text-xs text-zinc-400">
                                  • {issue.message}
                                </p>
                              ))}
                            </div>
                          )}
                          {entity.override_reason && (
                            <p className="mt-2 text-xs text-amber-400/80 italic">
                              Override: {entity.override_reason}
                            </p>
                          )}
                        </div>
                        <div className={`px-3 py-1 rounded-full ${getIntegrityScoreBg(entity.integrity_score)}`}>
                          <span className={`text-sm font-bold ${getIntegrityScoreColor(entity.integrity_score)}`}>
                            {entity.integrity_score}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}
